import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * A list of short claims, each with a small leading icon. Appearance is
 * `.icon-list` in `styles/ui.css`.
 *
 * **`shrink-0` on the icon is the part that matters.** Without it a claim long
 * enough to wrap compresses the icon into an ellipse instead of wrapping the
 * text, which only shows up at one narrow window width and never in review.
 *
 * `tone` is the icon's colour, not the text's - the text follows the surface via
 * `className`, the icon is the accent. They differ on every one of these lists,
 * which is why they are two decisions rather than one.
 */
export function IconList({
  items,
  tone = "accent",
  layout = "wrap",
  align = "start",
  className,
}: {
  items: { icon: React.ComponentType<{ className?: string }>; text: React.ReactNode }[];
  tone?: "accent" | "panel-accent" | "ok" | "subtle";
  layout?: "wrap" | "stack";
  align?: "start" | "center";
  className?: string;
}) {
  return (
    <ul
      className={cn(
        "icon-list",
        `icon-list--${tone}`,
        layout === "stack" && "icon-list--stack",
        align === "center" && "icon-list--center",
        className
      )}
    >
      {items.map(({ icon: Icon, text }, index) => (
        <li key={index} className="icon-list__item">
          <Icon />
          {text}
        </li>
      ))}
    </ul>
  );
}
